import {
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Collections as CollectionsIcon,
  FilterFrames as FilterFramesIcon,
  Image as ImageIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardMedia,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { hostname } from "../utils";
import LoadingButton from "./LoadingButton";

interface EmbeddedImagePickerProps {
  onSelect: (imageUrl: string, imageId?: string) => void;
  getToken: () => Promise<string>;
  companyId: string;
  contentId?: string;
  selectedImage?: string;
  height?: number | string;
  showAiGenerate?: boolean;
  showApplyFrame?: boolean;
  showStockImages?: boolean;
}

interface StockImage {
  id: string;
  provider: "unsplash" | "pexels";
  description?: string;
  photographer?: string;
  photographerUrl?: string;
  thumbnailUrl?: string;
  previewUrl?: string;
  fullUrl?: string;
  downloadUrl?: string;
  width?: number;
  height?: number;
  color?: string;
}

interface ImageModel {
  id: string;
  name: string;
  provider: string;
  tags?: string[];
  imageRef?: boolean;
  default_width?: number;
  default_height?: number;
}

interface BrandImage {
  id: string;
  name: string;
  description?: string;
  images?: Array<{ url: string; type?: string }>;
  imageModels?: string[];
  imageStylePrompt?: string;
  templates?: Array<{
    id: string;
    url: string;
    name: string;
    width?: number;
    height?: number;
  }>;
}

interface ContentImage {
  id: string;
  url: string;
  prompt: string;
  imageId: string;
  provider?: string;
}

interface CompanyImage {
  id: string;
  url: string;
  publicUrl: string;
  prompt: string;
  created: number;
  provider?: string;
}

const EmbeddedImagePicker: React.FC<EmbeddedImagePickerProps> = ({
  onSelect,
  getToken,
  companyId,
  contentId,
  selectedImage,
  height = 400,
  showAiGenerate = true,
  showApplyFrame = true,
  showStockImages = true,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [contentImages, setContentImages] = useState<ContentImage[]>([]);
  const [companyImages, setCompanyImages] = useState<CompanyImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState<string>(selectedImage || "");
  const [selectedId, setSelectedId] = useState<string>("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string>("");
  const [uploadDescription, setUploadDescription] = useState<string>("");
  const [error, setError] = useState<string>("");

  // AI Generate state
  const [imageModels, setImageModels] = useState<ImageModel[]>([]);
  const [brandImages, setBrandImages] = useState<BrandImage[]>([]);
  const [generatePrompt, setGeneratePrompt] = useState("");
  const [generateModel, setGenerateModel] = useState("");
  const [selectedBrandImage, setSelectedBrandImage] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState("");
  const [generatedImageId, setGeneratedImageId] = useState("");

  // Apply Frame state
  const [frameSourceImage, setFrameSourceImage] = useState<string>("");
  const [frameSourceImageId, setFrameSourceImageId] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [selectedBrandForFrame, setSelectedBrandForFrame] = useState<string>("");
  const [applyingFrame, setApplyingFrame] = useState(false);

  // Stock Images state
  const [stockSearchQuery, setStockSearchQuery] = useState("");
  const [debouncedStockQuery, setDebouncedStockQuery] = useState("");
  const [stockImages, setStockImages] = useState<StockImage[]>([]);
  const [stockSearching, setStockSearching] = useState(false);
  const [stockPage, setStockPage] = useState(1);
  const [stockHasMore, setStockHasMore] = useState(true);
  const [stockLoadingMore, setStockLoadingMore] = useState(false);
  const [downloadingStockId, setDownloadingStockId] = useState<string>("");
  const [selectedStockId, setSelectedStockId] = useState<string>("");
  const stockSearchTimeout = useRef<NodeJS.Timeout | null>(null);

  // Calculate dynamic tab indices based on which tabs are shown
  const getTabIndex = useCallback(
    (tabName: string): number => {
      let index = 0;
      const tabs = [
        { name: "contentImages", show: !!contentId },
        { name: "mediaLibrary", show: true },
        { name: "upload", show: true },
        { name: "stockImages", show: showStockImages },
        { name: "aiGenerate", show: showAiGenerate },
        { name: "applyFrame", show: showApplyFrame },
      ];

      for (const tab of tabs) {
        if (tab.name === tabName) return index;
        if (tab.show) index++;
      }
      return -1;
    },
    [contentId, showStockImages, showAiGenerate, showApplyFrame]
  );

  const fetchContentImages = useCallback(async () => {
    if (!contentId) return;

    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/content/${contentId}/images`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }
      );
      setContentImages(response.data.images || []);
    } catch (err) {
      console.error("Error fetching content images:", err);
      setError("Failed to load content images");
    } finally {
      setLoading(false);
    }
  }, [contentId, companyId, getToken]);

  const fetchCompanyImages = useCallback(
    async (pageNum: number = 1, append: boolean = false) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError("");
      try {
        const token = await getToken();
        const response = await axios.get(`${hostname}/images`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            companyId,
            page: pageNum,
            pageSize: 12,
          },
        });
        const newImages = response.data.items || [];

        if (append) {
          setCompanyImages((prev) => [...prev, ...newImages]);
        } else {
          setCompanyImages(newImages);
        }

        setHasMore(response.data.has_next || false);
      } catch (err) {
        console.error("Error fetching company images:", err);
        setError("Failed to load company images");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [companyId, getToken]
  );

  const fetchImageModels = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/api/v0/images/models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImageModels(response.data || []);
      if (response.data.length > 0 && !generateModel) {
        setGenerateModel(response.data[0].id);
      }
    } catch (err) {
      console.error("Error fetching image models:", err);
    }
  }, [getToken, generateModel]);

  const fetchBrandImages = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/brand-images`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyId, pageSize: 100 },
      });
      setBrandImages(response.data.items || []);
    } catch (err) {
      console.error("Error fetching brand images:", err);
    }
  }, [getToken, companyId]);

  const searchStockImages = useCallback(
    async (query: string, pageNum: number = 1, append: boolean = false) => {
      if (!query.trim()) return;

      if (append) {
        setStockLoadingMore(true);
      } else {
        setStockSearching(true);
        setStockImages([]);
      }
      setError("");

      try {
        const token = await getToken();
        const response = await axios.get(`${hostname}/stock-images/search`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            query: query.trim(),
            page: pageNum,
            perPage: 12,
          },
        });

        const results = response.data.results || [];

        if (append) {
          setStockImages((prev) => [...prev, ...results]);
        } else {
          setStockImages(results);
        }

        setStockHasMore(results.length === 12);
        setStockPage(pageNum);
      } catch (err) {
        console.error("Error searching stock images:", err);
        setError("Failed to search stock images");
      } finally {
        setStockSearching(false);
        setStockLoadingMore(false);
      }
    },
    [getToken]
  );

  const downloadStockImage = useCallback(
    async (image: StockImage) => {
      setDownloadingStockId(image.id);
      setError("");

      try {
        const token = await getToken();
        const response = await axios.post(
          `${hostname}/stock-images/download`,
          {
            photoId: image.id,
            provider: image.provider,
            companyId,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const downloadedImage = response.data;
        // Track which stock image was selected and update selection state
        setSelectedStockId(image.id);
        setSelected(downloadedImage.url || downloadedImage.publicUrl);
        setSelectedId(downloadedImage.id);
        onSelect(
          downloadedImage.url || downloadedImage.publicUrl,
          downloadedImage.id
        );
      } catch (err) {
        console.error("Error downloading stock image:", err);
        setError("Failed to download image");
      } finally {
        setDownloadingStockId("");
      }
    },
    [getToken, companyId, onSelect]
  );

  useEffect(() => {
    if (tabValue === getTabIndex("contentImages") && contentId) {
      fetchContentImages();
    } else if (tabValue === getTabIndex("mediaLibrary")) {
      // Reset pagination when switching to company images tab
      setPage(1);
      setCompanyImages([]);
      fetchCompanyImages(1, false);
    }
  }, [tabValue, fetchContentImages, fetchCompanyImages, contentId, getTabIndex]);

  // Fetch models and brand images when AI Generate or Apply Frame tabs are active
  useEffect(() => {
    if (
      (showAiGenerate && tabValue === getTabIndex("aiGenerate")) ||
      (showApplyFrame && tabValue === getTabIndex("applyFrame"))
    ) {
      if (imageModels.length === 0) {
        fetchImageModels();
      }
      if (brandImages.length === 0) {
        fetchBrandImages();
      }
    }
  }, [
    tabValue,
    getTabIndex,
    showAiGenerate,
    showApplyFrame,
    fetchImageModels,
    fetchBrandImages,
    imageModels.length,
    brandImages.length,
  ]);

  useEffect(() => {
    setSelected(selectedImage || "");
  }, [selectedImage]);

  // Debounce stock search query
  useEffect(() => {
    // Clear any existing timeout
    if (stockSearchTimeout.current) {
      clearTimeout(stockSearchTimeout.current);
    }

    // If query is empty, clear results immediately
    if (!stockSearchQuery.trim()) {
      setDebouncedStockQuery("");
      setStockImages([]);
      setSelectedStockId("");
      return;
    }

    // Set a new timeout to update the debounced query after 500ms
    stockSearchTimeout.current = setTimeout(() => {
      setDebouncedStockQuery(stockSearchQuery.trim());
    }, 500);

    // Cleanup on unmount or when query changes
    return () => {
      if (stockSearchTimeout.current) {
        clearTimeout(stockSearchTimeout.current);
      }
    };
  }, [stockSearchQuery]);

  // Trigger search when debounced query changes
  useEffect(() => {
    if (debouncedStockQuery) {
      searchStockImages(debouncedStockQuery, 1, false);
    }
  }, [debouncedStockQuery, searchStockImages]);

  // Infinite scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold

      // Media Library infinite scroll
      if (
        isNearBottom &&
        hasMore &&
        !loadingMore &&
        !loading &&
        tabValue === getTabIndex("mediaLibrary")
      ) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchCompanyImages(nextPage, true);
      }

      // Stock Images infinite scroll
      if (
        isNearBottom &&
        stockHasMore &&
        !stockLoadingMore &&
        !stockSearching &&
        debouncedStockQuery &&
        tabValue === getTabIndex("stockImages")
      ) {
        const nextPage = stockPage + 1;
        searchStockImages(debouncedStockQuery, nextPage, true);
      }
    },
    [
      hasMore,
      loadingMore,
      loading,
      page,
      fetchCompanyImages,
      tabValue,
      getTabIndex,
      stockHasMore,
      stockLoadingMore,
      stockSearching,
      debouncedStockQuery,
      stockPage,
      searchStockImages,
    ]
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError("");
  };

  const handleImageSelect = (url: string, imageId?: string) => {
    setSelected(url);
    setSelectedId(imageId || "");
    // Clear stock image selection when selecting from other tabs
    setSelectedStockId("");
    onSelect(url, imageId);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }
      setUploadFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError("");
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    setError("");
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append("image", uploadFile);
      formData.append("companyId", companyId);
      const trimmedDescription = uploadDescription.trim();
      if (trimmedDescription) {
        formData.append("prompt", trimmedDescription);
      }

      const response = await axios.post(`${hostname}/images/upload?companyId=${companyId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const uploadedImage = response.data;
      handleImageSelect(
        uploadedImage.publicUrl || uploadedImage.url,
        uploadedImage.id
      );
      setUploadFile(null);
      setUploadPreview("");
      setUploadDescription("");
      setTabValue(getTabIndex("mediaLibrary"));
    } catch (err) {
      console.error("Error uploading image:", err);
      setError("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!generatePrompt || !generateModel) {
      setError("Please enter a prompt and select a model");
      return;
    }

    setGenerating(true);
    setError("");
    setGeneratedImageUrl("");
    setGeneratedImageId("");

    try {
      const token = await getToken();

      // Build the request body
      const requestBody: any = {
        prompt: generatePrompt,
        model: generateModel,
        companyId,
      };

      // If a brand image is selected, get its style prompt and reference images
      if (selectedBrandImage) {
        const brandImage = brandImages.find((b) => b.id === selectedBrandImage);
        if (brandImage) {
          // Append brand style prompt to the user prompt
          if (brandImage.imageStylePrompt) {
            requestBody.prompt = `${generatePrompt}. Style: ${brandImage.imageStylePrompt}`;
          }

          // Add reference images from brand if the model supports them
          const selectedModel = imageModels.find(
            (m) => m.id === generateModel
          );
          if (
            selectedModel?.imageRef &&
            brandImage.images &&
            brandImage.images.length > 0
          ) {
            requestBody.referenceImages = brandImage.images
              .slice(0, 3)
              .map((img) => img.url);
          }
        }
      }

      // Set model-specific parameters
      const selectedModel = imageModels.find((m) => m.id === generateModel);
      if (selectedModel) {
        if (
          selectedModel.provider === "Leonardo AI" &&
          selectedModel.default_width
        ) {
          requestBody.width = selectedModel.default_width;
          requestBody.height = selectedModel.default_height;
        }
      }

      await axios.post(`${hostname}/images`, requestBody, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Poll for the generated image in the library
      // The backend generates images asynchronously, so we'll switch to the library tab
      setGeneratePrompt("");
      setSelectedBrandImage("");

      // Switch to media library tab to see the generated image
      setTabValue(getTabIndex("mediaLibrary"));
      setPage(1);
      setCompanyImages([]);
      fetchCompanyImages(1, false);
    } catch (err: any) {
      console.error("Error generating image:", err);
      setError(
        err.response?.data?.error ||
          "Failed to generate image. Please try again."
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleApplyFrame = async () => {
    if (!frameSourceImage || !selectedTemplate) {
      setError("Please select a source image and a template");
      return;
    }

    setApplyingFrame(true);
    setError("");

    try {
      const token = await getToken();

      const response = await axios.post(
        `${hostname}/images/apply-template`,
        {
          sourceImageUrl: frameSourceImage,
          templateId: selectedTemplate,
          brandImageId: selectedBrandForFrame,
          companyId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const resultImage = response.data;
      handleImageSelect(
        resultImage.publicUrl || resultImage.url,
        resultImage.id
      );

      // Reset state
      setFrameSourceImage("");
      setFrameSourceImageId("");
      setSelectedTemplate("");
      setSelectedBrandForFrame("");

      // Switch to media library to see the result
      setTabValue(getTabIndex("mediaLibrary"));
      setPage(1);
      setCompanyImages([]);
      fetchCompanyImages(1, false);
    } catch (err: any) {
      console.error("Error applying frame:", err);
      setError(
        err.response?.data?.error || "Failed to apply frame. Please try again."
      );
    } finally {
      setApplyingFrame(false);
    }
  };

  // Get templates from the selected brand image for frame application
  const getAvailableTemplates = () => {
    if (!selectedBrandForFrame) return [];
    const brandImage = brandImages.find((b) => b.id === selectedBrandForFrame);
    return brandImage?.templates || [];
  };

  // Filter models based on selected brand image
  const getAvailableModels = () => {
    if (!selectedBrandImage) return imageModels;
    const brandImage = brandImages.find((b) => b.id === selectedBrandImage);
    if (!brandImage?.imageModels || brandImage.imageModels.length === 0) {
      return imageModels;
    }
    return imageModels.filter((m) => brandImage.imageModels?.includes(m.id));
  };

  const renderImageGrid = (images: any[], isContentImages: boolean = false, isLoadingMore: boolean = false) => {
    // Show empty state only when not loading and no images
    if (images.length === 0 && !loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <Typography color="textSecondary">No images available</Typography>
        </Box>
      );
    }

    return (
      <>
        <Grid container spacing={2}>
          {images.map((image) => {
            const imageUrl = isContentImages
              ? image.url
              : image.publicUrl || image.url;
            const isSelected = selected === imageUrl;

            return (
              <Grid item xs={12} sm={6} md={4} key={image.id}>
                <Card
                  sx={{
                    border: isSelected ? 2 : 1,
                    borderStyle: "solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    boxShadow: isSelected ? 3 : 1,
                    position: "relative",
                  }}
                >
                  <CardActionArea
                    onClick={() => handleImageSelect(imageUrl, image.imageId || image.id)}
                  >
                    <CardMedia
                      component="img"
                      height="150"
                      image={imageUrl}
                      alt={image.prompt || "Image"}
                      sx={{ objectFit: "cover" }}
                    />
                    <Box p={1}>
                      {image.prompt && (
                        <Typography variant="caption" display="block" noWrap>
                          {image.prompt}
                        </Typography>
                      )}
                      {image.provider && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {image.provider}
                        </Typography>
                      )}
                    </Box>
                  </CardActionArea>
                  {isSelected && (
                    <CheckCircleIcon
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "primary.main",
                        backgroundColor: "white",
                        borderRadius: "50%",
                      }}
                    />
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
        {/* Single linear progress bar for any loading state */}
        {(loading || isLoadingMore) && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: height, minHeight: 0 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{ mb: 2, flexShrink: 0 }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {contentId && (
          <Tab
            icon={<ImageIcon />}
            label="Content Images"
            iconPosition="start"
          />
        )}
        <Tab
          icon={<CollectionsIcon />}
          label="Media Library"
          iconPosition="start"
        />
        <Tab
          icon={<CloudUploadIcon />}
          label="Upload Image"
          iconPosition="start"
        />
        {showStockImages && (
          <Tab
            icon={<PhotoLibraryIcon />}
            label="Stock Images"
            iconPosition="start"
          />
        )}
        {showAiGenerate && (
          <Tab
            icon={<AutoAwesomeIcon />}
            label="AI Generate"
            iconPosition="start"
          />
        )}
        {showApplyFrame && (
          <Tab
            icon={<FilterFramesIcon />}
            label="Apply Frame"
            iconPosition="start"
          />
        )}
      </Tabs>

      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }} onScroll={handleScroll}>
        {tabValue === getTabIndex("contentImages") && contentId && (
          <Box>{renderImageGrid(contentImages, true, false)}</Box>
        )}

        {tabValue === getTabIndex("mediaLibrary") && (
          <Box>
            {renderImageGrid(companyImages, false, loadingMore)}
            {!hasMore && companyImages.length > 0 && !loading && !loadingMore && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Typography variant="caption" color="text.secondary">
                  No more images to load
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {tabValue === getTabIndex("upload") && (
          <Box>
            <Box
              sx={{
                border: "2px dashed #ccc",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
              }}
              onClick={() => document.getElementById("upload-input")?.click()}
            >
              <input
                id="upload-input"
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: "#999", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Click to upload an image
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Supported formats: JPG, PNG, GIF, WebP (Max 10MB)
              </Typography>
            </Box>

            {uploadPreview && (
              <Box mt={3}>
                <Typography variant="subtitle1" gutterBottom>
                  Preview:
                </Typography>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={uploadPreview}
                    alt="Upload preview"
                    sx={{ objectFit: "contain" }}
                  />
                </Card>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Description (optional)"
                  placeholder="e.g. residents playing an activity, food service"
                  helperText="Describe what's in the image. Used for searching and AI context."
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  sx={{ mt: 2 }}
                />
                <Box mt={2} display="flex" justifyContent="center">
                  <LoadingButton
                    onClick={handleUpload}
                    variant="contained"
                    loading={uploading}
                    startIcon={<CloudUploadIcon />}
                  >
                    Upload & Select
                  </LoadingButton>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Stock Images Tab */}
        {showStockImages && tabValue === getTabIndex("stockImages") && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search free stock images from Unsplash and Pexels.
            </Typography>

            {/* Search Input */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Search stock images"
                placeholder="e.g., nature, business, technology..."
                value={stockSearchQuery}
                onChange={(e) => setStockSearchQuery(e.target.value)}
                InputProps={{
                  endAdornment: stockSearchQuery ? (
                    <SearchIcon color="action" />
                  ) : null,
                }}
              />
            </Box>

            {/* Stock Images Grid */}
            {stockImages.length === 0 && !stockSearching ? (
              <Box display="flex" justifyContent="center" p={4}>
                <Typography color="text.secondary">
                  {stockSearchQuery && debouncedStockQuery
                    ? "No images found. Try a different search term."
                    : "Start typing to search stock images..."}
                </Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {stockImages.map((image) => {
                    const isSelected = selectedStockId === image.id;
                    const isDownloading = downloadingStockId === image.id;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={image.id}>
                        <Card
                          sx={{
                            border: isSelected ? 2 : 1,
                            borderStyle: "solid",
                            borderColor: isSelected ? "primary.main" : "divider",
                            boxShadow: isSelected ? 3 : 1,
                            position: "relative",
                          }}
                        >
                          <CardActionArea
                            onClick={() => downloadStockImage(image)}
                            disabled={isDownloading}
                          >
                            <CardMedia
                              component="img"
                              height="150"
                              image={image.thumbnailUrl || image.previewUrl}
                              alt={image.description || "Stock image"}
                              sx={{ objectFit: "cover" }}
                            />
                            <Box p={1}>
                              {image.photographer && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  noWrap
                                >
                                  by {image.photographer}
                                </Typography>
                              )}
                              <Chip
                                label={image.provider}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          </CardActionArea>
                          {isDownloading && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255,255,255,0.8)",
                              }}
                            >
                              <CircularProgress size={32} />
                            </Box>
                          )}
                          {isSelected && !isDownloading && (
                            <CheckCircleIcon
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                color: "primary.main",
                                backgroundColor: "white",
                                borderRadius: "50%",
                              }}
                            />
                          )}
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                {(stockLoadingMore || stockSearching) && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress />
                  </Box>
                )}
                {!stockHasMore && stockImages.length > 0 && !stockSearching && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      No more images to load
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* AI Generate Tab */}
        {showAiGenerate && tabValue === getTabIndex("aiGenerate") && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate a new image using AI. Optionally apply brand styling for
              consistent visuals.
            </Typography>

            {/* Brand Image Selector */}
            {brandImages.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Brand Style (Optional)</InputLabel>
                <Select
                  value={selectedBrandImage}
                  label="Brand Style (Optional)"
                  onChange={(e) => {
                    setSelectedBrandImage(e.target.value);
                    // Reset model if the selected brand has specific models
                    const newBrandImage = brandImages.find(
                      (b) => b.id === e.target.value
                    );
                    if (
                      newBrandImage?.imageModels &&
                      newBrandImage.imageModels.length > 0 &&
                      !newBrandImage.imageModels.includes(generateModel)
                    ) {
                      setGenerateModel(newBrandImage.imageModels[0]);
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {brandImages.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Model Selector */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>AI Model</InputLabel>
              <Select
                value={generateModel}
                label="AI Model"
                onChange={(e) => setGenerateModel(e.target.value)}
              >
                {getAvailableModels().map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip label={model.provider} size="small" />
                      {model.tags?.map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                      <span>{model.name}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Prompt Input */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Image Prompt"
              placeholder="Describe the image you want to generate..."
              value={generatePrompt}
              onChange={(e) => setGeneratePrompt(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* Selected Brand Style Preview */}
            {selectedBrandImage && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: "action.hover",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Brand style will be applied:{" "}
                  {
                    brandImages.find((b) => b.id === selectedBrandImage)
                      ?.imageStylePrompt
                  }
                </Typography>
              </Box>
            )}

            <Box display="flex" justifyContent="center">
              <LoadingButton
                onClick={handleGenerateImage}
                variant="contained"
                loading={generating}
                disabled={!generatePrompt || !generateModel}
                startIcon={<AutoAwesomeIcon />}
              >
                Generate Image
              </LoadingButton>
            </Box>
          </Box>
        )}

        {/* Apply Frame Tab */}
        {showApplyFrame && tabValue === getTabIndex("applyFrame") && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Apply a template frame overlay to an existing image. Select a
              brand image collection to access its templates.
            </Typography>

            {/* Brand Image Selector for Templates */}
            {brandImages.length > 0 ? (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Brand Image Collection</InputLabel>
                <Select
                  value={selectedBrandForFrame}
                  label="Select Brand Image Collection"
                  onChange={(e) => {
                    setSelectedBrandForFrame(e.target.value);
                    setSelectedTemplate("");
                  }}
                >
                  <MenuItem value="">
                    <em>Select a brand image collection</em>
                  </MenuItem>
                  {brandImages
                    .filter((b) => b.templates && b.templates.length > 0)
                    .map((brand) => (
                      <MenuItem key={brand.id} value={brand.id}>
                        {brand.name} ({brand.templates?.length} template
                        {brand.templates?.length !== 1 ? "s" : ""})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No brand images with templates found. Create a brand image
                collection with template overlays first.
              </Alert>
            )}

            {/* Template Selector */}
            {selectedBrandForFrame && getAvailableTemplates().length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Template
                </Typography>
                <Grid container spacing={1}>
                  {getAvailableTemplates().map((template) => (
                    <Grid item xs={6} sm={4} key={template.id}>
                      <Card
                        sx={{
                          border: selectedTemplate === template.id ? 2 : 1,
                          borderStyle: "solid",
                          borderColor:
                            selectedTemplate === template.id
                              ? "primary.main"
                              : "divider",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedTemplate(template.id)}
                      >
                        <CardMedia
                          component="img"
                          height="80"
                          image={template.url}
                          alt={template.name}
                          sx={{
                            objectFit: "contain",
                            backgroundColor: "action.hover",
                          }}
                        />
                        <Box p={0.5}>
                          <Typography variant="caption" noWrap>
                            {template.name}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Source Image Selection */}
            {selectedTemplate && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Source Image
                </Typography>
                {frameSourceImage ? (
                  <Box>
                    <Card sx={{ mb: 1 }}>
                      <CardMedia
                        component="img"
                        height="150"
                        image={frameSourceImage}
                        alt="Source image"
                        sx={{ objectFit: "contain" }}
                      />
                    </Card>
                    <LoadingButton
                      size="small"
                      onClick={() => {
                        setFrameSourceImage("");
                        setFrameSourceImageId("");
                      }}
                    >
                      Change Image
                    </LoadingButton>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Select an image from Media Library or upload a new one:
                    </Typography>
                    {companyImages.length > 0 ? (
                      <Grid container spacing={1}>
                        {companyImages.slice(0, 6).map((image) => (
                          <Grid item xs={4} sm={3} key={image.id}>
                            <Card
                              sx={{ cursor: "pointer" }}
                              onClick={() => {
                                setFrameSourceImage(
                                  image.publicUrl || image.url
                                );
                                setFrameSourceImageId(image.id);
                              }}
                            >
                              <CardMedia
                                component="img"
                                height="60"
                                image={image.publicUrl || image.url}
                                alt="Select image"
                                sx={{ objectFit: "cover" }}
                              />
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No images in library. Upload an image first.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Apply Button */}
            {selectedTemplate && frameSourceImage && (
              <Box display="flex" justifyContent="center">
                <LoadingButton
                  onClick={handleApplyFrame}
                  variant="contained"
                  loading={applyingFrame}
                  startIcon={<FilterFramesIcon />}
                >
                  Apply Frame & Select
                </LoadingButton>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EmbeddedImagePicker;
